import {createPhotoPreview, normalizedLines} from './apolis-photo-preview.js?v=215216af39fa9a10';
import manifest from './apolis-photo-manifest.js?v=c5c82c486bd8aabe';
import {ThemeEvents} from '@theme/events';

export function paragraphSubmissionCheck({config, raw, noCustom, signature, readySignature, previewState}) {
  const paragraph = String(config?.web?.is_para ?? '0').split('__').some(value => value === '1');
  if (!paragraph || noCustom || raw.every(value => !String(value).trim())) return {allowed:true, guarded:paragraph};
  let normalized;
  try { normalized = normalizedLines(config, raw); }
  catch (error) { return {allowed:false, guarded:true, message:error.message}; }
  if (!signature || signature !== readySignature || previewState !== 'ready') {
    return {allowed:false, guarded:true, message:'Your note must fit in the printable area. Please wait for a successful preview or edit your text.'};
  }
  return {allowed:true, guarded:true, normalized};
}

let active, host, lastSignature = '', readySignature = '', scheduled, zoomUrl, zoomRecord, retryButton, requestId = 0;
const style = document.createElement('style');
style.textContent = `
.apolis-photo-host{position:relative!important}
.apolis-photo-canvas{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;z-index:1;pointer-events:none}
.apolis-photo-status{font:12px/1.4 sans-serif;color:inherit;margin:8px 0;text-align:center}
.apolis-photo-status:empty{display:none}
.apolis-photo-retry[hidden]{display:none!important}
.apolis-personalization-feedback{font:12px/1.4 sans-serif;color:inherit;margin:8px 0 0;text-align:left}
.apolis-personalization-feedback:empty{display:none}
`;
document.head.append(style);

function restoreZoom() {
  if(zoomRecord){
    const {image,attributes,visibility}=zoomRecord;
    for(const [key,value] of Object.entries(attributes)) {
      if(value===null)image.removeAttribute(key);else image.setAttribute(key,value);
    }
    image.style.visibility=visibility;zoomRecord=null;
  }
  if(zoomUrl){URL.revokeObjectURL(zoomUrl);zoomUrl=null;}
}
function clear() {
  requestId++;
  restoreZoom();
  active?.destroy();active=null;
  host?.querySelector('.apolis-photo-canvas')?.remove();
  document.querySelector('.apolis-photo-status')?.remove();
  retryButton?.remove();retryButton=null;
  host=null;lastSignature='';readySignature='';
}
function feedback(fields=document.querySelector('.apolis-lines')) {
  if (!fields) return null;
  let message = fields.parentElement?.querySelector(':scope > .apolis-personalization-feedback');
  if (!message) {
    message = document.createElement('p');
    message.className = 'apolis-personalization-feedback';
    message.setAttribute('role','status');
    message.setAttribute('aria-live','polite');
    fields.after(message);
  }
  return message;
}
function setFeedback(message, fields) {
  const node = feedback(fields);
  if (node) node.textContent = message;
}
function setReadyFeedback(no, raw, fields) {
  if(no) setFeedback('No custom text selected. Preview updated above.', fields);
  else if(raw.some(line=>line.trim())) setFeedback('Preview updated above.', fields);
  else setFeedback('Add your text below. Your preview updates above as you type.', fields);
}
function focusPersonalizationFields() {
  const fields = document.querySelector('.apolis-lines');
  const input = fields?.querySelector('input[type="text"]:not([disabled])');
  if (!input) return null;
  input.focus({preventScroll:true});
  input.scrollIntoView({behavior:'smooth',block:'center'});
  setFeedback('Add your text below. Your preview updates above as you type.', fields);
  return input;
}
// Native form validity prevents spelling confirmation for a pending or invalid note.
// Recheck the exact current variant/text at submission, including scripted changes.
const ownedValidity = new WeakMap();
function currentParagraphState() {
  const configElement = document.querySelector('.apolis-cust-config');
  const fields = document.querySelector('.apolis-lines');
  const form = document.querySelector('.apolis-custprop')?.form;
  if (!configElement || !fields || !form) return null;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return null; }
  config.variant_id = form.elements.namedItem('id')?.value;
  const noCustom = !!document.querySelector('.apolis-blank-seg button[data-blank="yes"].on') ||
    !!document.querySelector('variant-picker input[value="No Custom Text"]:checked');
  const inputs = [...fields.querySelectorAll('input[type="text"]')];
  const raw = inputs.map(input => input.disabled ? '' : input.value);
  const sample = !noCustom && raw.every(line => !line.trim());
  return {config, fields, form, inputs, raw, noCustom, signature:JSON.stringify([config,raw,noCustom,sample])};
}
function paragraphValidity(pending = false, errorMessage = '') {
  const state = currentParagraphState();
  if (!state) return {allowed:true};
  const result = paragraphSubmissionCheck({...state, readySignature:pending ? '' : readySignature,
    previewState:host?.querySelector('.apolis-photo-canvas')?.dataset.previewState});
  for (const input of state.inputs) {
    const previous = ownedValidity.get(input);
    if (previous && input.validationMessage === previous) input.setCustomValidity('');
    ownedValidity.delete(input);
  }
  if (!result.allowed) {
    const input = state.inputs.find(input => !input.disabled);
    const message = errorMessage || result.message;
    if (input && !input.validity.customError) {
      input.setCustomValidity(message); ownedValidity.set(input,message);
    }
    return {...result, form:state.form, fields:state.fields, message};
  }
  return {...result, form:state.form, fields:state.fields};
}
window.addEventListener('submit', event => {
  const state = currentParagraphState();
  if (!state || event.target !== state.form) return;
  const result = paragraphValidity();
  if (!result.allowed) {
    event.preventDefault(); event.stopImmediatePropagation();
    setFeedback(result.message, result.fields);
    result.form.reportValidity();
  }
},true);

function update(detail) {
  const configElement = document.querySelector('.apolis-cust-config');
  if (!configElement) {clear();return;}
  let config;
  try {config = JSON.parse(configElement.textContent);} catch {clear();return;}
  if (!config?.handle) {clear();return;}
  const productForm = document.querySelector('.apolis-custprop')?.form;
  config.variant_id = productForm?.elements.namedItem('id')?.value;
  const nextHost = document.querySelector('media-gallery .product-media');
  const fields = document.querySelector('.apolis-lines');
  if (!nextHost || !fields) {clear();return;}
  feedback(fields);
  const no = !!document.querySelector('.apolis-blank-seg button[data-blank="yes"].on') ||
    !!document.querySelector('variant-picker input[value="No Custom Text"]:checked');
  const raw = [...fields.querySelectorAll('input[type="text"]')].map(input=>input.disabled?'':input.value);
  // Product pages start with their original sample-lettered photograph. Keep
  // sample artwork out of inputs/cart properties; any real text replaces it.
  // Explicit No Custom Text still renders the blank photo and fixed artwork.
  const sample = !no && raw.every(line => !line.trim());
  const mulberry = [10406551093434,10406551388346,10406551650490,10409686958266].includes(Number(config.product_id));
  const sampleLines = mulberry ? ['YOUR','TEXT','HERE'] : manifest.profiles[config.handle]?.sample_lines;
  if (sample && !sampleLines) {clear();return;}
  const previewLines = sample ? sampleLines : raw;
  if (host !== nextHost || !active) {
    clear();
    host = nextHost;
    host.classList.add('apolis-photo-host');
    const canvas = document.createElement('canvas');
    canvas.className = 'apolis-photo-canvas';
    canvas.hidden = true;
    canvas.setAttribute('role','img');
    canvas.setAttribute('aria-label','Your personalized bag preview');
    host.append(canvas);
    const status = document.createElement('p');
    status.className = 'apolis-photo-status';
    status.setAttribute('role','status');
    status.setAttribute('aria-live','polite');
    fields.after(status);
    retryButton=document.createElement('button');
    retryButton.type='button';retryButton.className='apolis-photo-retry button button-secondary';
    retryButton.textContent='Retry preview';retryButton.hidden=true;
    retryButton.addEventListener('click',()=>{lastSignature='';schedule();});
    status.after(retryButton);
    active = createPhotoPreview({canvas,status,profiles:manifest.profiles,fonts:manifest.fonts,fontMetrics:manifest.fontMetrics});
    lastSignature = '';
    readySignature = '';
  }
  const signature = JSON.stringify([config,raw,no,sample]);
  paragraphValidity(signature !== readySignature);
  if (signature === lastSignature) {
    if (signature === readySignature) setReadyFeedback(no, raw, fields);
    return;
  }
  restoreZoom();
  lastSignature = signature;
  retryButton.hidden=true;
  const renderer = active;
  const request = ++requestId;
  const canvas = host.querySelector('.apolis-photo-canvas');
  active.update(config, previewLines, no).then(ready=>{
    if(request!==requestId || active!==renderer || lastSignature!==signature)return;
    if(!ready){
      lastSignature='';readySignature='';retryButton.hidden=false;
      const message = document.querySelector('.apolis-photo-status')?.textContent || 'Preview could not update. Use Retry preview to try again.';
      paragraphValidity(false, message);
      setFeedback(message, fields);
      return;
    }
    readySignature=signature;
    paragraphValidity();
    setReadyFeedback(no, raw, fields);
    // The native zoom dialog keeps its own image nodes. Synchronize the first
    // photograph at original resolution while retaining every other gallery item.
    canvas.toBlob(blob=>{
      if(!blob || request!==requestId || active!==renderer || lastSignature!==signature)return;
      const dialog = host.closest('media-gallery')?.querySelector('.dialog-zoomed-gallery__dialog');
      const image = dialog?.querySelector('.product-media img');
      if(!image)return;
      zoomRecord={image,visibility:image.style.visibility,attributes:Object.fromEntries(
        ['src','srcset','data_max_resolution','width','height','alt'].map(key=>[key,image.getAttribute(key)]))};
      zoomUrl=URL.createObjectURL(blob);
      image.removeAttribute('srcset');image.src=zoomUrl;
      image.setAttribute('data_max_resolution',zoomUrl);
      image.width=canvas.width;image.height=canvas.height;
      image.alt=canvas.getAttribute('aria-label');
    },'image/png');
  });
}
function schedule() {paragraphValidity(true);clearTimeout(scheduled);scheduled=setTimeout(update,30);}
document.addEventListener('apolis:personalization',schedule);
document.addEventListener(ThemeEvents.variantUpdate,schedule);
document.addEventListener('input',event=>{
  if(!event.target.closest('.apolis-lines'))return;
  setFeedback('Updating your preview above…');
  schedule();
});
document.addEventListener('change',event=>{if(event.target.closest('variant-picker'))schedule();});
document.addEventListener('click',event=>{
  const segment=event.target.closest('.apolis-blank-seg');
  if(segment) schedule();
  const segmentAdd=event.target.closest('.apolis-blank-seg button[data-blank="no"]');
  const variantLabel=event.target.closest('.variant-option__button-label');
  const variantAdd=variantLabel?.querySelector('input[value="Add Custom Text"]');
  if(!segmentAdd&&!variantAdd)return;
  setTimeout(()=>{
    const focused=focusPersonalizationFields();
    setTimeout(()=>{if(!focused?.isConnected)focusPersonalizationFields();},250);
  },0);
});
new MutationObserver(records=>{
  if(records.some(record=>[...record.addedNodes,...record.removedNodes].some(node=>node.nodeType===1 &&
    (node.matches?.('media-gallery, .product-media, .apolis-lines, .apolis-cust-config, product-information') ||
     node.querySelector?.('media-gallery, .product-media, .apolis-lines, .apolis-cust-config')))))schedule();
}).observe(document.body,{childList:true,subtree:true});
update();
